import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PlanRecipeDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { PlanColorDot } from './PlanColorDot';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { planColor } from '../../lib/planColors';
import {
  cookIngredientLines,
  loadCounterTicks,
  saveCounterTicks,
  scaleLabel,
  toggleTick,
} from '../../lib/plans';

/**
 * Cooking mode's ingredient list: one group per recipe on the plan, under the
 * recipe's colour chip, scaled like the rest of the plan. Each line can be
 * ticked "out on the counter"; the ticks live in sessionStorage per plan so a
 * reload mid-cook keeps them (same as the step timers).
 */
export function CookIngredients({
  planId,
  recipes,
  system,
  defaultOpen,
}: {
  planId: string;
  recipes: readonly PlanRecipeDto[];
  system: 'us' | 'metric';
  /** Open on arrival before cooking has started; collapsed once it is under way. */
  defaultOpen: boolean;
}) {
  // Decided once, on arrival; afterwards the person opens and closes it.
  const [open, setOpen] = useState(defaultOpen);
  const [ticks, setTicks] = useState<string[]>(() => loadCounterTicks(planId));

  useEffect(() => {
    saveCounterTicks(planId, ticks);
  }, [planId, ticks]);

  const recipeIds = recipes.map((r) => r.recipeId);
  // Same key (and data) as the print view's full-recipe fetch.
  const details = useQuery({
    queryKey: queryKeys.plans.printRecipes(planId, recipeIds),
    queryFn: () => Promise.all(recipeIds.map((recipeId) => api.recipes.get(recipeId))),
    enabled: recipeIds.length > 0,
  });
  const detailById = new Map((details.data ?? []).map((r) => [r.id, r]));

  if (recipes.length === 0) return null;

  return (
    <details
      className="card p-3"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="min-h-11 cursor-pointer content-center text-lg font-semibold text-brand">
        Ingredients
      </summary>
      {details.isPending ? (
        <p className="mt-2 text-base text-muted">Loading ingredients…</p>
      ) : details.isError ? (
        <Alert tone="error" className="mt-2">
          {messageForError(details.error)}
        </Alert>
      ) : (
        <div className="mt-2 flex flex-col gap-4">
          {recipes.map((recipe) => {
            const detail = detailById.get(recipe.recipeId);
            const lines = detail
              ? cookIngredientLines(detail.ingredients, recipe.scale, system)
              : [];
            const color = planColor(recipe.colorIndex);
            return (
              <section key={recipe.recipeId} aria-label={`${recipe.title} ingredients`}>
                <h2
                  className="flex flex-wrap items-center gap-2 text-lg"
                  style={{ color: color.ink }}
                >
                  <PlanColorDot colorIndex={recipe.colorIndex} label={recipe.title} size={14} />
                  {recipe.title}
                  <span className="text-sm font-normal text-muted">{scaleLabel(recipe.scale)}</span>
                </h2>
                {lines.length === 0 ? (
                  <p className="mt-1 text-base text-muted">No ingredients recorded.</p>
                ) : (
                  <ul className="mt-1 flex flex-col">
                    {lines.map((line) => {
                      const ticked = ticks.includes(line.id);
                      return (
                        <li
                          key={line.id}
                          className="border-b border-dashed border-line last:border-b-0"
                        >
                          <label className="flex min-h-12 cursor-pointer items-center gap-3 py-2 text-lg text-ink">
                            <input
                              type="checkbox"
                              className="size-6 shrink-0 accent-[var(--color-brand)]"
                              checked={ticked}
                              onChange={() => setTicks((t) => toggleTick(t, line.id))}
                            />
                            <span className={`min-w-0 ${ticked ? 'text-muted line-through' : ''}`}>
                              {line.amount ? (
                                <strong className="font-semibold text-brand">{line.amount}</strong>
                              ) : null}
                              {line.amount ? ' ' : ''}
                              {line.name}
                              {line.prepNote ? (
                                <span className="text-muted">, {line.prepNote}</span>
                              ) : null}
                              {line.optional ? (
                                <span className="text-muted"> (optional)</span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </details>
  );
}
